import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const BASE_URL = "https://mayawears.com.np";
const DEFAULT_IMAGE = `${BASE_URL}/logo-1.webp`;
const DEFAULT_TITLE = "Maya Wears | Crafting Creativity";
const DEFAULT_DESCRIPTION = "Shop contemporary streetwear, signature oversized tees, jackets, and timeless apparel at Maya Wears Nepal. Designed with restraint, made to remain.";
const DEFAULT_KEYWORDS = "Maya Wears, streetwear Nepal, oversized t-shirts Nepal, contemporary fashion Kathmandu, buy clothes online Nepal, Nepali fashion brand, Maya clothing";

/**
 * Helper to update or create a meta tag
 */
const updateMetaTag = (attrName, attrValue, content) => {
  if (content === undefined || content === null) return;
  let element = document.querySelector(`meta[${attrName}="${attrValue}"]`);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attrName, attrValue);
    document.head.appendChild(element);
  }
  element.setAttribute("content", content);
};

/**
 * Helper to update or create a link tag (e.g. canonical)
 */
const updateLinkTag = (rel, href) => {
  if (!href) return;
  let element = document.querySelector(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement("link");
    element.setAttribute(rel ? "rel" : "href", rel);
    element.setAttribute("rel", rel);
    document.head.appendChild(element);
  }
  element.setAttribute("href", href);
};

/**
 * Strips HTML tags and excessive whitespace for clean meta descriptions
 */
export const stripHtml = (html) => {
  if (!html) return "";
  return html
    .replace(/<[^>]*>?/gm, " ")
    .replace(/\s+/g, " ")
    .trim();
};

/**
 * SEO Component: Declarative, zero-dependency head and JSON-LD schema manager
 */
const SEO = ({
  title,
  description,
  keywords,
  canonical,
  ogType = "website",
  ogImage,
  noindex = false,
  schema = null,
}) => {
  const location = useLocation();

  useEffect(() => {
    // 1. Page Title
    let formattedTitle = DEFAULT_TITLE;
    if (title && title.trim().length > 0) {
      if (title.toLowerCase().includes("maya wears")) {
        formattedTitle = title.trim();
      } else {
        formattedTitle = `${title.trim()} | Maya Wears`;
      }
    }
    document.title = formattedTitle;

    // 2. Standard Meta Tags
    const metaDesc = description ? stripHtml(description).slice(0, 160) : DEFAULT_DESCRIPTION;
    updateMetaTag("name", "description", metaDesc);
    updateMetaTag("name", "keywords", keywords || DEFAULT_KEYWORDS);

    // Robots meta (indexing directives)
    if (noindex) {
      updateMetaTag("name", "robots", "noindex, nofollow");
    } else {
      updateMetaTag("name", "robots", "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1");
    }

    // 3. Canonical URL
    const canonicalUrl = canonical
      ? canonical
      : `${BASE_URL}${location.pathname}`;
    updateLinkTag("canonical", canonicalUrl);

    // 4. Open Graph Tags (Facebook, WhatsApp, LinkedIn)
    const socialImage = ogImage || DEFAULT_IMAGE;
    updateMetaTag("property", "og:site_name", "Maya Wears");
    updateMetaTag("property", "og:type", ogType);
    updateMetaTag("property", "og:title", formattedTitle);
    updateMetaTag("property", "og:description", metaDesc);
    updateMetaTag("property", "og:url", canonicalUrl);
    updateMetaTag("property", "og:image", socialImage);
    updateMetaTag("property", "og:locale", "en_NP");

    // 5. Twitter Card Tags
    updateMetaTag("name", "twitter:card", "summary_large_image");
    updateMetaTag("name", "twitter:title", formattedTitle);
    updateMetaTag("name", "twitter:description", metaDesc);
    updateMetaTag("name", "twitter:image", socialImage);

    // 6. JSON-LD Structured Data
    const schemaScriptId = "maya-structured-data";
    let scriptTag = document.getElementById(schemaScriptId);

    if (schema) {
      if (!scriptTag) {
        scriptTag = document.createElement("script");
        scriptTag.id = schemaScriptId;
        scriptTag.type = "application/ld+json";
        document.head.appendChild(scriptTag);
      }
      scriptTag.textContent = JSON.stringify(schema);
    } else if (scriptTag) {
      // Remove schema if page doesn't specify one
      scriptTag.remove();
    }

    return () => {
      // Cleanup on unmount if needed
    };
  }, [title, description, keywords, canonical, ogType, ogImage, noindex, schema, location.pathname]);

  return null;
};

export default SEO;
